/**
 * What a class-diagram box says and how big it is: the stereotype («interface», «library»...),
 * the name, a compartment with the language and the node's description, the first picture
 * attached to the node (a character's portrait, a screen's mock-up), and one row per pin
 * (a blueprint's ways out: a scene's choices). The class diagram and the blueprint measure
 * boxes the same, so a box keeps its place switching from one to the other. Sizes are in world
 * units (the diagram's zoom scales them), measured with the fonts the painter uses.
 */
import type { FieldDef } from "@protocol/FieldDef";
import type { Kinds } from "@protocol/Kinds";
import type { Node } from "@protocol/Node";
import type { Pin } from "@protocol/Pin";
import { nodeKindOf } from "../../shared/kinds";
import { pictureOf } from "../../shared/assets";
import { boxValues } from "../../shared/fields";

/** Measures the width of `text` drawn with `font` (a CSS font), in world units. */
export type Measure = (text: string, font: string) => number;

/** The fonts of a box, as CSS fonts (the family comes from the theme). */
export interface BoxFonts {
  /** The stereotype line. */
  stereotype: string;
  /** The name. */
  name: string;
  /** The name of an abstract class (italic, as UML writes it). */
  abstractName: string;
  /** The compartment's lines. */
  detail: string;
}

/** The fonts of a box with the theme's family `family`. */
export function boxFonts(family: string): BoxFonts {
  const f = family || "sans-serif";
  return { stereotype: `11px ${f}`, name: `600 13px ${f}`, abstractName: `italic 600 13px ${f}`, detail: `11.5px ${f}` };
}

/** Room around the text inside a box. */
export const BOX_PAD = 10;
/** Height of the stereotype line. */
export const STEREOTYPE_LINE = 14;
/** Height of the name line. */
export const NAME_LINE = 18;
/** Height of a compartment line. */
export const DETAIL_LINE = 15;
/** The narrowest box. */
export const MIN_BOX_WIDTH = 120;
/** The widest box: longer descriptions wrap. */
export const MAX_BOX_WIDTH = 240;
/** At most this many lines of description; the last one ends with "…" when cut. */
export const MAX_NOTE_LINES = 3;
/** Height of a box's picture. */
export const PICTURE_H = 90;
/** Height of a pin's row. */
export const PIN_LINE = 20;
/** Room a pin's row keeps beside its label for the pin itself. */
const PIN_ROOM = 16;

/** A box's text, laid out in lines. */
export interface BoxText {
  /** «interface», «library»... (`null` for a plain class). */
  stereotype: string | null;
  /** The node's name. */
  name: string;
  /** Written in italic (an abstract class). */
  italic: boolean;
  /** The compartment: the values of the environment's fields written in boxes (a language), then the description in lines (may be empty). */
  details: string[];
  /** How many of `details` are field values (drawn stronger than the description). */
  values: number;
}

/** A measured box: its text and its size. */
export interface BoxShape extends BoxText {
  /** Width, world units. */
  w: number;
  /** Height, world units. */
  h: number;
  /** Height of the header (stereotype and name); the compartment is below it. */
  header: number;
  /** The node's pins, in order, one row each at the bottom of the box. */
  pins: Pin[];
  /** Where the pins' rows start, from the box's top (its height when it has no pins). */
  pinsTop: number;
  /** The picture drawn in the box: the first image attached to the node (its path), if any. */
  picture: string | null;
  /** Where the picture starts, from the box's top. */
  pictureTop: number;
}

/** What a pin's row says: its label, else the id. */
export const pinText = (pin: Pin) => pin.label.trim() || pin.id;

/**
 * Cuts `text` into lines no wider than `width`, at most `max` of them; a cut last line ends
 * with "…". Words longer than a line are cut too.
 */
export function wrap(text: string, width: number, font: string, measure: Measure, max: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const tried = line ? `${line} ${word}` : word;
    if (measure(tried, font) <= width) {
      line = tried;
      continue;
    }
    if (line) lines.push(line);
    line = word;
    while (measure(line, font) > width && line.length > 1) {
      let cut = line.length - 1;
      while (cut > 1 && measure(line.slice(0, cut), font) > width) cut--;
      lines.push(line.slice(0, cut));
      line = line.slice(cut);
    }
  }
  if (line) lines.push(line);
  if (lines.length <= max) return lines;
  const kept = lines.slice(0, max);
  let last = `${kept[max - 1]}…`;
  while (last.length > 1 && measure(last, font) > width) last = `${last.slice(0, -2)}…`;
  kept[max - 1] = last;
  return kept;
}

/**
 * Measures the box of `node`; its kind (one of `kinds`) gives its stereotype and whether its
 * name is in italic, and its environment's `fields` which values it writes (those `on_box`).
 */
export function measureBox(node: Pick<Node, "name" | "kind" | "data" | "note"> & { pins?: Pin[]; attachments?: string[] }, fonts: BoxFonts, measure: Measure, kinds?: Kinds, fields: readonly FieldDef[] = []): BoxShape {
  const { stereotype, italic } = nodeKindOf(kinds, node);
  const inner = MAX_BOX_WIDTH - 2 * BOX_PAD;
  const nameFont = italic ? fonts.abstractName : fonts.name;
  const name = wrap(node.name, inner, nameFont, measure, 1)[0] ?? node.name;
  const values = boxValues(node, fields);
  const details = [...values, ...wrap(node.note, inner, fonts.detail, measure, MAX_NOTE_LINES)];
  const pins = (node.pins ?? []).map((p) => ({ id: p.id, label: p.label }));
  const widest = Math.max(
    measure(name, nameFont),
    stereotype ? measure(stereotype, fonts.stereotype) : 0,
    ...details.map((d) => measure(d, fonts.detail)),
    ...pins.map((p) => measure(pinText(p), fonts.detail) + PIN_ROOM),
  );
  const w = Math.round(Math.min(MAX_BOX_WIDTH, Math.max(MIN_BOX_WIDTH, widest + 2 * BOX_PAD)));
  const header = BOX_PAD + (stereotype ? STEREOTYPE_LINE : 0) + NAME_LINE + BOX_PAD / 2;
  const picture = pictureOf(node.attachments);
  const compartment = header + (details.length ? BOX_PAD / 2 + details.length * DETAIL_LINE + BOX_PAD / 2 : BOX_PAD / 2);
  const pictureTop = Math.round(compartment);
  const pinsTop = Math.round(picture ? compartment + PICTURE_H + BOX_PAD / 2 : compartment);
  const h = pins.length ? pinsTop + pins.length * PIN_LINE + BOX_PAD / 2 : pinsTop;
  return { stereotype, name, italic, details, values: values.length, w, h, header, pins, pinsTop, picture, pictureTop };
}
